// import { authApi } from 'api'
import { Auth } from 'aws-amplify'
import {
  saveUserInfoToStore,
  getUserInfoFromStore,
  deleteUserInfoFromStore,
} from '../../utils/storage'

import { openDatabase, initDatabase } from '../../utils/sqlite'

import { setCurrentLanguage } from 'translation'
import * as actionTypes from './actionTypes'
import { fetchAllMusic } from './playerActions'

export const authStart = () => {
  return {
    type: actionTypes.AUTH_START,
  }
}

export const authSuccess = (user, attributes) => async (dispatch) => {
  // TODO: Remove when add fetching of settings from backend or attributes
  const userSettings = getSettings(user.username)
  const payload = {
    user,
    saveFiles: !!userSettings.saveFiles,
  }

  if (user.signInUserSession && attributes) payload.attributes = attributes

  const language =
    userSettings?.language || attributes?.language || user?.storage?.i18nextLng
  payload.language =
    !language || !['fr', 'en'].includes(language.toLowerCase()) ? 'fr' : language

  dispatch({
    type: actionTypes.AUTH_SUCCESS,
    payload,
  })
  // Passing 'true' parameter will prevent reload on login
  dispatch(setLanguage(payload.language, true))
}

export const authFail = (error) => (dispatch) => {
  return dispatch({
    type: actionTypes.AUTH_FAIL,
    payload: error,
  })
}

export const socialSignIn = (provider) => () => {
  Auth.federatedSignIn({ provider })
}

export const resetAuthError = () => (dispatch) => {
  return dispatch({ type: actionTypes.AUTH_ERROR_RESET })
}

export const logOut = () => async (dispatch) => {
  try {
    if (window?.cordova) await deleteUserInfoFromStore()

    await Auth.signOut()
    return dispatch({
      type: actionTypes.AUTH_LOGOUT,
    })
  } catch (e) {
    dispatch(authFail(e))
    console.log('error signing out: ', e)
  }
}

// TODO: Add auto log-out?
export const checkAuthTimeout = (expirationTime) => (dispatch) => {
  setTimeout(() => {
    dispatch(logOut())
  }, expirationTime * 1000)
}

export const logIn =
  ({ email, password }) =>
  async (dispatch, getState) => {
    dispatch(authStart())
    try {
      const user = await Auth.signIn(email, password)

      if (user.signInUserSession) {
        const attributes = await getParseAttributes(user, getState().auth.isOnline)

        if (window?.cordova) {
          await saveUserInfoToStore(user)
          const db = await openDatabase()
          await initDatabase(db, `sessions_${attributes?.localId}`)
          dispatch({ type: actionTypes.SET_SESSION_DB, payload: db })
          dispatch({
            type: actionTypes.SET_SESSION_DB_TABLE,
            payload: `sessions_${attributes?.localId}`,
          })
        }
        // if (!user.signInUserSession?.idToken?.payload?.userCreatedDate) {
        //   if (process.env.NODE_ENV !== 'production') {
        //     console.log('Error: No user created date')
        //     console.log(
        //       'user.signInUserSession.idToken.payload',
        //       user?.signInUserSession?.idToken?.payload
        //     )
        //   }

        //   localStorage.clear()
        //   return dispatch(logOut())
        // }
        dispatch(authSuccess(user, attributes))
      } else {
        // Will launch setting new password if logging in for the first time
        dispatch(authSuccess(user, null))
      }

      return true
    } catch (err) {
      console.log('Error logging in', err)
      dispatch(authFail(err))
      return false
    }
  }

export const setOwnInitialPassword =
  ({ email, tempPassword, ownPassword }) =>
  async (dispatch, getState) => {
    try {
      const userWithTempPassword = await Auth.signIn(email, tempPassword)

      if (userWithTempPassword?.challengeName !== 'NEW_PASSWORD_REQUIRED')
        throw Error('Invalid user login attempt')

      const user = await Auth.completeNewPassword(userWithTempPassword, ownPassword)
      const attributes = await getParseAttributes(user, getState().auth.isOnline)
      dispatch(authSuccess(user, attributes))
      return true
    } catch (err) {
      console.log('Error logging in', err)
      dispatch(authFail(err))
      return false
    }
  }

export const setNewPassword =
  ({ password: newPassword }) =>
  async (dispatch, getState) => {
    const userWithTempPassword = getState().auth.user
    dispatch(authStart())
    try {
      const user = await Auth.completeNewPassword(userWithTempPassword, newPassword)
      const attributes = await getParseAttributes(user, getState().auth.isOnline)

      dispatch(authSuccess(user, attributes))
    } catch (err) {
      console.log('Error setting new password:', err)
      dispatch(authFail(err))
    }
  }

export const changePassword =
  (currentPassword, newPassword) => async (dispatch, getState) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(user, currentPassword, newPassword)

      return { success: true }
    } catch (e) {
      console.log('Error changing password: ', e.message)
      dispatch(authFail(e))
    }
  }

// TODO: To implement, if necessary
// export const setAuthRedirectPath = (path) => {
//   return {
//     type: actionTypes.SET_AUTH_REDIRECT_PATH,
//     path: path,
//   }
// }

export const initialAutoAuth = () => async (dispatch, getState) => {
  dispatch(authStart())

  try {
    let user

    if (getState().auth.isOnline) {
      user = await Auth.currentAuthenticatedUser()
      if (window?.cordova) await saveUserInfoToStore(user)
    } else user = await getUserInfoFromStore()

    const attributes = await getParseAttributes(user, getState().auth.isOnline)

    if (window?.cordova) {
      const db = await openDatabase()
      await initDatabase(db, `sessions_${attributes?.localId}`)
      dispatch({ type: actionTypes.SET_SESSION_DB, payload: db })
      dispatch({
        type: actionTypes.SET_SESSION_DB_TABLE,
        payload: `sessions_${attributes?.localId}`,
      })
    }
    // if (!user?.signInUserSession?.idToken?.payload?.userCreatedDate) {
    //   if (process.env.NODE_ENV !== 'production') {
    //     console.log('Error: No user created date')
    //     console.log(
    //       'user.signInUserSession.idToken.payload',
    //       user?.signInUserSession?.idToken?.payload
    //     )
    //   }

    //   localStorage.clear()
    //   return dispatch(logOut())
    // }
    dispatch(authSuccess(user, attributes))
    return true
  } catch (e) {
    // If not logged in, and everything else is ok,
    // expect a 'The user is not authenticated' message so do not record it as error
    dispatch(authFail(e !== 'The user is not authenticated' ? e : null))
    return false
  }
}

const saveSettings = (settingsToUpdate, userId) => {
  const settingsItem = `settings_${userId}`
  const settings = JSON.parse(localStorage.getItem(settingsItem)) || {}
  const updatedSettings = { ...settings, ...settingsToUpdate }
  localStorage.setItem(settingsItem, JSON.stringify(updatedSettings))
}

const getSettings = (userId) => {
  const settings = JSON.parse(localStorage.getItem(`settings_${userId}`)) || {}
  return settings
}

export const setLanguage = (language, noReload) => (dispatch, getState) => {
  const userId = getState().auth.userId
  saveSettings({ language }, userId)
  dispatch({ type: actionTypes.SET_LANGUAGE, payload: language })
  setCurrentLanguage(language, noReload)
}

export const getParseAttributes = async (user, isOnline) => {
  const attributesToDecode = process.env.USER_ATTRIBUTES_TO_DECODE.split(',')

  const attributesArr = isOnline
    ? await Auth.userAttributes(user)
    : Object.entries(user.attributes).map(([key, val]) => ({ Name: key, Value: val }))

  const parsedAttributes = attributesArr
    .filter(({ Name }) => Name !== 'identities')
    .reduce((acc, { Name, Value }) => {
      const parsedName = Name.replace(/^custom:/, '')

      acc[parsedName] = attributesToDecode.includes(parsedName)
        ? JSON.parse(decodeURIComponent(Value))
        : Value

      return acc
    }, {})

  // Add additional properties not in cognito attributes for ease of access
  const userCreatedDate = user.signInUserSession?.idToken?.payload?.userCreatedDate
  const subscribed = user.signInUserSession?.idToken?.payload?.subscribed
  const updatePayment = user.signInUserSession?.idToken?.payload?.updatePayment
  if (userCreatedDate) parsedAttributes.userCreatedDate = userCreatedDate
  if (subscribed) parsedAttributes.subscribed = JSON.parse(subscribed)
  if (
    (parsedAttributes.hasOwnProperty('prescriberCode') &&
      parsedAttributes.hasOwnProperty('otherInfo')) ||
    parsedAttributes['otherInfo']?.hasOwnProperty('prescriberCode')
  )
    parsedAttributes['prescriberCode'] = parsedAttributes['otherInfo']['prescriberCode']
  if (updatePayment) parsedAttributes.updatePayment = JSON.parse(updatePayment)

  return parsedAttributes
}

export const forgotPasswordSubmitEmail =
  ({ email }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.AUTH_START })
    try {
      await Auth.forgotPassword(email)
      dispatch({ type: actionTypes.AUTH_RESET_PASS_SUCCESS })
      return true
    } catch (e) {
      console.log('Error resetting password when submit email:', e)
      dispatch({ type: actionTypes.AUTH_FAIL, payload: e.message })
      return false
    }
  }

export const forgotPasswordSubmitCode =
  ({ email, code, newPassword }) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.AUTH_START })
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword)
      dispatch({ type: actionTypes.AUTH_RESET_PASS_SUCCESS })

      // Password reset successfully, log user in
      dispatch(logIn({ email, password: newPassword }))
    } catch (e) {
      console.log('Error setting a new password during reset:', e)
      dispatch({ type: actionTypes.AUTH_FAIL, payload: e.message })
      return false
    }
  }

export const setAuthSocialMessage =
  (message = '') =>
  (dispatch) =>
    dispatch({ type: actionTypes.AUTH_SOCIAL_MSG, payload: message })

export const setIsOnlineState = (isOnline) => (dispatch, getState) => {
  dispatch({ type: actionTypes.SET_ONLINE_STATE, payload: isOnline })

  if (!window.cordova) return

  const isAuthenticated = getState().auth.isAuthenticated

  if (!isAuthenticated) return

  const networkState = navigator.connection.type

  const states = {}
  states[window.Connection.UNKNOWN] = 'Unknown connection'
  states[window.Connection.ETHERNET] = 'Ethernet connection'
  states[window.Connection.WIFI] = 'WiFi connection'
  states[window.Connection.CELL_2G] = 'Cell 2G connection'
  states[window.Connection.CELL_3G] = 'Cell 3G connection'
  states[window.Connection.CELL_4G] = 'Cell 4G connection'
  states[window.Connection.CELL] = 'Cell generic connection'
  states[window.Connection.NONE] = 'No network connection'

  // console.log('Connection type: ' + states[networkState])

  dispatch(fetchAllMusic())
}

export const authRefreshToken = () => async (dispatch) => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser()
    const currentSession = await cognitoUser.signInUserSession
    await cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {
      if (session) console.log(`Session refreshed`)
    })
    dispatch({ type: actionTypes.AUTH_REFRESH_TOKEN })
  } catch (e) {
    dispatch({ type: actionTypes.AUTH_FAIL, payload: e.message })
    return false
  }
}
